// Browser support warning.
//
// Most of the app's tone system uses color-mix(), and roughly all of
// those calls reference CSS variables like var(--gold). Lightningcss
// polyfills color-mix() at build time only when the arguments are
// literal colors — a call like color-mix(in srgb, var(--gold) 20%,
// transparent) can't be resolved statically, so it ships as-is. On
// Safari older than 16.2 (Dec 2022) those declarations silently drop
// out and every card / gradient / KPI surface loses its background,
// which the user perceives as "misaligned UI."
//
// This banner detects the missing capability once at mount and shows
// a small persistent, dismissible message directing the user to
// update their browser or switch to one that supports the feature.
// Dismissal is remembered in localStorage per-major-Safari-version
// so if the user updates, the banner clears itself automatically.

import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const STORAGE_KEY = "gabruz-browser-support-dismissed";

function isColorMixSupported() {
  if (typeof CSS === "undefined" || typeof CSS.supports !== "function") return true;
  try {
    return CSS.supports("color", "color-mix(in srgb, red, blue)");
  } catch {
    return true;
  }
}

// Include a coarse UA signature in the dismissal key so upgrading the
// browser wipes the "dismissed" state and re-shows the banner if the
// new browser also lacks support (unlikely, but keeps the check fresh).
function uaSignature() {
  try {
    return navigator.userAgent.replace(/[^a-zA-Z0-9]/g, "").slice(0, 60);
  } catch { return "unknown"; }
}

export default function BrowserSupportBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isColorMixSupported()) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === uaSignature()) return;
    } catch { /* storage disabled — show anyway */ }
    setVisible(true);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="browserSupportBanner"
      role="status"
      aria-live="polite"
    >
      <AlertTriangle size={16}/>
      <div className="browserSupportBanner-body">
        <b>Your browser is out of date.</b>
        <span>
          Some colors and cards won't render correctly. Please update Safari
          to 16.2 or newer (macOS Ventura +) — or open this site in Chrome or Firefox.
        </span>
      </div>
      <button
        type="button"
        className="browserSupportBanner-close"
        aria-label="Dismiss"
        onClick={() => {
          try { localStorage.setItem(STORAGE_KEY, uaSignature()); } catch { /* ignore */ }
          setVisible(false);
        }}
      >
        <X size={14}/>
      </button>
    </div>
  );
}
